import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { API_URL, CSRF_HEADER } from './config'
import { ApiError } from './errors'
import { clearAccessToken, getAccessToken, notifyUnauthorized, setAccessToken } from './session'
import type { LoginResponse } from '@milliswift/types'

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete'])

/**
 * The shared axios instance.
 *
 * - sends the refresh cookie (`withCredentials`), which is HttpOnly
 * - attaches the in-memory access token as a bearer
 * - adds the CSRF header on state-changing verbs, which the platform's CORS
 *   layer pre-allows
 * - on 401, refreshes once and replays the request
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  // The platform answers fast; a hung request should surface, not stall a page.
  timeout: 30_000,
})

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }

  const method = (config.method ?? 'get').toLowerCase()
  if (MUTATING_METHODS.has(method)) {
    config.headers.set(CSRF_HEADER, '1')
  }

  return config
})

/**
 * A refresh in flight, shared by every caller.
 *
 * Without this, a page that fires six requests on mount would trigger six
 * refreshes on expiry; because the platform rotates the cookie on every
 * refresh, five of them would then fail and sign the user out.
 */
let refreshInFlight: Promise<string> | null = null

/**
 * Uses a bare axios call rather than `apiClient`, so a 401 from the refresh
 * endpoint cannot recurse back into this interceptor.
 */
function refreshAccessToken(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post<LoginResponse>(`${API_URL}/auth/refresh`, null, { withCredentials: true })
      .then((response) => {
        const token = response.data.access_token
        if (!token) throw new Error('Refresh response did not include an access token')
        setAccessToken(token)
        return token
      })
      .finally(() => {
        refreshInFlight = null
      })
  }

  return refreshInFlight
}

interface RetryableConfig extends AxiosRequestConfig {
  /** Set once a request has already been replayed after a refresh. */
  _retried?: boolean
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(ApiError.from(error))

    const config = error.config as RetryableConfig | undefined
    const status = error.response?.status
    const url = config?.url ?? ''

    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh')

    if (status === 401 && config && !config._retried && !isAuthEndpoint) {
      config._retried = true

      try {
        await refreshAccessToken()
        return apiClient(config)
      } catch {
        // Refresh failed: the session is genuinely over.
        clearAccessToken()
        notifyUnauthorized()
        return Promise.reject(ApiError.from(error))
      }
    }

    if (status === 401 && !isAuthEndpoint) {
      clearAccessToken()
      notifyUnauthorized()
    }

    return Promise.reject(ApiError.from(error))
  }
)

/** Refresh explicitly — used on app boot to restore a session from the cookie. */
export async function restoreSession(): Promise<string | null> {
  try {
    return await refreshAccessToken()
  } catch {
    return null
  }
}
